import type { ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { de } from '@/i18n/de';
import { useNavigation } from './navigation';

export interface TabHeaderProps {
  title: string;
  /** Small line under the title, e.g. the current date on the "Aktuell" tab. */
  subtitle?: string;
  /** Controls placed left of the settings icon (arrows, "Heute", legend). */
  actions?: ReactNode;
  /** The settings icon lives in the sidebar on desktop, so it is hidden there. */
  showSettings?: boolean;
  className?: string;
}

/**
 * Screen header. Every tab renders its own so the arrangement can differ, while
 * the title metrics and the settings entry point stay consistent.
 */
export function TabHeader({
  title,
  subtitle,
  actions,
  showSettings = true,
  className,
}: TabHeaderProps) {
  const { setAdminOpen } = useNavigation();

  return (
    <header className={cn('flex items-start justify-between gap-3 px-4 pt-3.5 pb-1', className)}>
      <div className="min-w-0">
        <h1 className="truncate text-screen font-bold text-fg">{title}</h1>
        {subtitle ? <p className="pt-0.5 text-label text-fg-hint">{subtitle}</p> : null}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {actions}
        {showSettings ? (
          <button
            type="button"
            aria-label={de.a11y.openAdmin}
            onClick={() => setAdminOpen(true)}
            className="hit-44 flex size-[30px] items-center justify-center rounded-cell text-fg-tertiary"
          >
            <SlidersHorizontal className="size-5" />
          </button>
        ) : null}
      </div>
    </header>
  );
}
